import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { vehicleAPI } from '../../api/vehicles';
import { CreateVehicleRequest } from '../../types/api';
import { FuelType, Vehicle } from '../../types/entities';
import toast from 'react-hot-toast';
import { Fuel, Bell, Info } from 'lucide-react';
import { VehicleReminders } from './VehicleReminders';

interface VehicleFormData extends CreateVehicleRequest {
  fuelNorm?: number;
  fuelType?: FuelType;
  currentOdometer?: number;
}

export const VehicleForm: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'reminders'>('info');
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<VehicleFormData>({
    defaultValues: {
      status: 'ACTIVE',
      fuelType: 'DIESEL',
    },
  });

  useEffect(() => {
    if (isEdit && id) {
      loadVehicle(parseInt(id));
    }
  }, [id, isEdit]);

  const loadVehicle = async (vehicleId: number) => {
    try {
      const vehicleData = await vehicleAPI.getById(vehicleId);
      setVehicle(vehicleData);
      reset({
        licensePlate: vehicleData.licensePlate,
        type: vehicleData.type || '',
        capacity: vehicleData.capacity || undefined,
        status: vehicleData.status || 'ACTIVE',
        fuelNorm: vehicleData.fuelNorm || undefined,
        fuelType: vehicleData.fuelType || 'DIESEL',
        currentOdometer: vehicleData.currentOdometer || undefined,
      });
    } catch (error) {
      toast.error(t('vehicles.loadError'));
      navigate('/vehicles');
    }
  };

  const onSubmit = async (data: VehicleFormData) => {
    setLoading(true);
    try {
      if (isEdit && id) {
        await vehicleAPI.update(parseInt(id), data);
        toast.success(t('vehicles.updateSuccess'));
      } else {
        await vehicleAPI.create(data);
        toast.success(t('vehicles.createSuccess'));
      }
      navigate('/vehicles');
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.unknown'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">
        {isEdit ? t('vehicles.editVehicle') : t('vehicles.addNew')}
      </h1>

      {/* Tabs for Edit Mode */}
      {isEdit && (
        <div className="flex space-x-1 mb-6 border-b">
          <button
            type="button"
            onClick={() => setActiveTab('info')}
            className={`flex items-center space-x-2 px-4 py-2 border-b-2 transition-colors ${
              activeTab === 'info'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Info className="w-4 h-4" />
            <span>{t('trips.basicInfo')}</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('reminders')}
            className={`flex items-center space-x-2 px-4 py-2 border-b-2 transition-colors ${
              activeTab === 'reminders'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Bell className="w-4 h-4" />
            <span>{t('reminders.title')}</span>
          </button>
        </div>
      )}

      {/* Reminders Tab Content */}
      {isEdit && activeTab === 'reminders' && vehicle && (
        <VehicleReminders
          vehicleId={parseInt(id!)}
          currentOdometer={vehicle.currentOdometer || 0}
        />
      )}

      {/* Info Tab Content / Create Form */}
      {(!isEdit || activeTab === 'info') && (
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Info */}
        <div className="card space-y-4">
          <h2 className="text-lg font-semibold">{t('trips.basicInfo')}</h2>
          
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('vehicles.licensePlate')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register('licensePlate', {
                required: t('validation.required'),
                pattern: {
                  value: /^[0-9]{2}[A-Z]-[0-9]{4,5}$/,
                  message: t('validation.invalidLicensePlate'),
                },
              })}
              className="input-field"
              placeholder="51A-12345"
            />
            {errors.licensePlate && (
              <p className="text-red-500 text-sm mt-1">{errors.licensePlate.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">{t('vehicles.type')}</label>
              <select {...register('type')} className="input-field">
                <option value="">{t('vehicles.selectType')}</option>
                <option value="TRUCK">{t('vehicles.types.TRUCK')}</option>
                <option value="DUMP_TRUCK">{t('vehicles.types.DUMP_TRUCK')}</option>
                <option value="CONTAINER">{t('vehicles.types.CONTAINER')}</option>
                <option value="TRAILER">{t('vehicles.types.TRAILER')}</option>
                <option value="PICKUP">{t('vehicles.types.PICKUP')}</option>
                <option value="VAN">{t('vehicles.types.VAN')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                {t('vehicles.capacity')} ({t('vehicles.capacityUnit')})
              </label>
              <input
                type="number"
                step="0.1"
                {...register('capacity', {
                  valueAsNumber: true,
                  min: { value: 0, message: t('validation.positiveNumber') },
                })}
                className="input-field"
                placeholder="5.5"
              />
              {errors.capacity && (
                <p className="text-red-500 text-sm mt-1">{errors.capacity.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">{t('common.status')}</label>
            <select {...register('status')} className="input-field">
              <option value="ACTIVE">{t('common.active')}</option>
              <option value="INACTIVE">{t('common.inactive')}</option>
            </select>
          </div>
        </div>

        {/* Fuel Control Section */}
        <div className="card space-y-4">
          <div className="flex items-center space-x-2">
            <Fuel className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold">{t('fuel.title')}</h2>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('vehicles.fuelNorm')} ({t('vehicles.fuelNormUnit')})
              </label>
              <input
                type="number"
                step="0.1"
                {...register('fuelNorm', {
                  valueAsNumber: true,
                  min: { value: 0, message: t('validation.positiveNumber') },
                })}
                className="input-field"
                placeholder="25.5"
              />
              {errors.fuelNorm && (
                <p className="text-red-500 text-sm mt-1">{errors.fuelNorm.message}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {t('vehicles.fuelNormUnit')} - Lít nhiên liệu tiêu thụ trên 100km
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{t('vehicles.fuelType')}</label>
              <select {...register('fuelType')} className="input-field">
                <option value="DIESEL">{t('vehicles.fuelTypes.DIESEL')}</option>
                <option value="GASOLINE">{t('vehicles.fuelTypes.GASOLINE')}</option>
              </select>
            </div>
          </div>

          {isEdit && (
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('vehicles.currentOdometer')} (km)
              </label>
              <input
                type="number"
                step="0.1"
                {...register('currentOdometer', {
                  valueAsNumber: true,
                  min: { value: 0, message: t('validation.positiveNumber') },
                })}
                className="input-field"
                placeholder="150000"
              />
              {errors.currentOdometer && (
                <p className="text-red-500 text-sm mt-1">{errors.currentOdometer.message}</p>
              )}
            </div>
          )}
        </div>

        {/* Submit */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/vehicles')}
            className="btn-secondary"
          >
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? t('common.loading') : isEdit ? t('common.save') : t('common.add')}
          </button>
        </div>
      </form>
      )}
    </div>
  );
};
